import * as C from "@sp-crm/core";
import { AsyncOperationId, InternalId, isAsyncOperationTerminal } from "@sp-crm/core";
import { trpcClient } from "components/rpc/trpc";
import { QueryClient } from "react-query";
import { Action, Dispatch } from "redux";
import { handleEmailSent } from "../../util/email-sent";
import * as http from "../../util/http";
import { Actions } from "./index";
import { makeRequestAction } from "./types";

export interface ClientIntakeDeliveryResult {
    accepted: string[];
    rejected: string[];
    code?: string;
}

export interface ClientIntakeEmailResponse {
    deliveryResults: ClientIntakeDeliveryResult[];
    canceled?: boolean;
    errorMessage?: string | null;
}

export interface ClientIntakeSendResult extends ClientIntakeEmailResponse {
    anyAccepted: boolean;
}

export const readSendOperationId = (data: unknown): AsyncOperationId | null => {
    const started = data as { operationId?: AsyncOperationId } | undefined;
    return started?.operationId ?? null;
};

const pollIntervalMs = 1000;
const maxPollWaitMs = 15 * 60 * 1000;
const maxPollAttempts = maxPollWaitMs / pollIntervalMs;

const delay = (ms: number): Promise<void> =>
    new Promise(resolve => setTimeout(resolve, ms));

export const refreshFeedWhenSendSettles = async (
    operationId: AsyncOperationId,
    queryClient: QueryClient,
): Promise<void> => {
    for (let attempt = 0; attempt < maxPollAttempts; attempt++) {
        const handle = await trpcClient.asyncOperation.get.query({ id: operationId });
        if (isAsyncOperationTerminal(handle.status)) {
            break;
        }
        await delay(pollIntervalMs);
    }
    handleEmailSent(queryClient);
};

export async function sendClientIntakeEmail(
    eventId: InternalId,
    messageDetails: C.ClientIntakeMessage,
    dispatch: Dispatch<Action>,
): Promise<void> {
    dispatch(makeRequestAction(Actions.EMAIL_FORM_SUBMIT, eventId));
    const requestDescription = http.newRequestDescription(
        "/api/trpc/clientIntakeNotifications.start",
    );

    try {
        const { operationId } = await trpcClient.clientIntakeNotifications.start.mutate({
            payload: messageDetails,
        });
        dispatch(
            makeRequestAction(Actions.EMAIL_FORM_OPERATION_STARTED, eventId, {
                operationId,
            }),
        );
    } catch (e) {
        http.errorHandler(e, requestDescription, dispatch);
        dispatch(
            makeRequestAction(Actions.EMAIL_FORM_ERROR, eventId, { deliveryResults: [] }),
        );
    }
}
